/**
 * The Lattice mark: nine supplement tablets on an ink tile, a 3x3 lattice. The nine are identical
 * circles on an exact grid with even gaps, so the grid itself is the lattice. Each is a muted
 * supplement colour from `@lattice/tokens/tablet.stylex`. From 32px up each carries one small
 * highlight in the same place, and three of them a fine score line, all horizontal, which is
 * enough to say tablet. The highlight sits above the score line and never crosses it.
 *
 * Below 32px the highlight and the score line are specks, so the mark is flat: nine plain circles
 * on the tile. The favicon and the email wordmark are that flat mark too. The colours hold 3:1
 * against the tile, and the tile has a faint light edge, so the mark reads the same on chalk, on the
 * deep teal hero and in the dark app, and does not follow `currentColor`. `forced-color-adjust` is
 * off because a logo keeps its own colours in a forced-colour mode.
 *
 * The one-colour mark is the tile in the text colour with the nine circles knocked out of it, for
 * a surface that has one ink to give. From 32px up the three score lines stay as ink bars across
 * their circles.
 *
 * It is decoration, hidden from assistive technology: the link or heading around it carries the name.
 * The one-colour mark below 32px has no score lines and so reads as a grid of dots, like a menu
 * icon. Use it beside the wordmark for print and one-ink surfaces only, never as a button.
 *
 * The tile's faint light edge is deliberate. It separates the tile from dark browser tabs and the
 * dark app, and the favicon draws the same edge at the same ratio.
 */
import * as stylex from "@stylexjs/stylex";
import { useId } from "react";
import "@lattice/tokens/tablet.stylex";
import { tablet } from "@lattice/tokens/tablet.stylex";
const fills = {
  calcium: {
    kDwRjp: "xwjx3g4",
    $$css: true
  },
  softgel: {
    kDwRjp: "x1t3l7p",
    $$css: true
  },
  slate: {
    kDwRjp: "x1qn06n",
    $$css: true
  },
  iron: {
    kDwRjp: "x1ipi377",
    $$css: true
  },
  mist: {
    kDwRjp: "x1f59pb9",
    $$css: true
  },
  turmeric: {
    kDwRjp: "x1oum8yq",
    $$css: true
  },
  sage: {
    kDwRjp: "xha3m0h",
    $$css: true
  },
  spirulina: {
    kDwRjp: "x9qm7xg",
    $$css: true
  },
  rosehip: {
    kDwRjp: "x1hoii0a",
    $$css: true
  }
};

/*
  Three columns in a 24-unit box: centres 6, 12 and 18, each circle 2.6 from its centre, so the gap
  between neighbours is 0.8 across and down. These are literals because they belong to this drawing
  alone. The scored ones run along a diagonal.
*/
const TABLETS = [{
  cx: 6,
  cy: 6,
  fill: fills.calcium,
  scored: false
}, {
  cx: 12,
  cy: 6,
  fill: fills.softgel,
  scored: false
}, {
  cx: 18,
  cy: 6,
  fill: fills.slate,
  scored: true
}, {
  cx: 6,
  cy: 12,
  fill: fills.iron,
  scored: false
}, {
  cx: 12,
  cy: 12,
  fill: fills.mist,
  scored: true
}, {
  cx: 18,
  cy: 12,
  fill: fills.turmeric,
  scored: false
}, {
  cx: 6,
  cy: 18,
  fill: fills.sage,
  scored: true
}, {
  cx: 12,
  cy: 18,
  fill: fills.spirulina,
  scored: false
}, {
  cx: 18,
  cy: 18,
  fill: fills.rosehip,
  scored: false
}] as const;
const RADIUS = 2.6;

/* Up and left of centre, clear of the score line through it. */
const GLINT_AT = 1.2;
const GLINT = 0.6;
const SCORE = 0.4;

/* The score line stops short of the circle edge, so it reads as a score and not a bar. */
const SCORE_INSET = 0.4;

/* From this size up the mark carries its highlights and score lines. */
const DETAIL_FROM = 32;
function Score({
  cx,
  cy,
  stroke
}: {
  readonly cx: number;
  readonly cy: number;
  readonly stroke: ReturnType<typeof stylex.props> | null;
}) {
  return <line x1={cx - RADIUS + SCORE_INSET} y1={cy} x2={cx + RADIUS - SCORE_INSET} y2={cy} strokeWidth={SCORE} stroke={stroke === null ? "white" : undefined} {...stroke ?? {}} />;
}
export function Mark({
  size = 24,
  tone = "colour"
}: {
  readonly size?: number;
  readonly tone?: "colour" | "one-colour";
}) {
  const id = useId();
  const detailed = size >= DETAIL_FROM;
  return tone === "colour" ? <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" data-testid="mark" className="x1kzknox">
      <rect x="0.25" y="0.25" width="23.5" height="23.5" rx="5" stroke="white" strokeOpacity="0.22" strokeWidth="0.5" className="x14vf2kw" />
      {TABLETS.map(({
      cx,
      cy,
      fill,
      scored
    }) => <g key={`${String(cx)}-${String(cy)}`}>
          <circle data-testid="tablet" cx={cx} cy={cy} r={RADIUS} {...stylex.props(fill)} />
          {detailed ? <circle cx={cx - GLINT_AT} cy={cy - GLINT_AT} r={GLINT} fill="white" fillOpacity="0.3" /> : null}
          {detailed && scored ? <Score cx={cx} cy={cy} stroke={{
        className: "x1bfdkbe"
      }} /> : null}
        </g>)}
    </svg> : <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" data-testid="mark">
      <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="white" />
        {TABLETS.map(({
        cx,
        cy
      }) => <circle key={`${String(cx)}-${String(cy)}`} cx={cx} cy={cy} r={RADIUS} fill="black" />)}
        {detailed ? TABLETS.flatMap(({
        cx,
        cy,
        scored
      }) => scored ? [<Score key={`${String(cx)}-${String(cy)}`} cx={cx} cy={cy} stroke={null} />] : []) : null}
      </mask>
      <rect width="24" height="24" rx="5" mask={`url(#${id})`} className="x117rol3" />
      {TABLETS.map(({
      cx,
      cy
    }) => <circle key={`${String(cx)}-${String(cy)}`} data-testid="tablet" cx={cx} cy={cy} r={RADIUS} fill="none" />)}
    </svg>;
}